import classNames from 'classnames'
import React, { Suspense } from 'react'
import { Outlet, matchPath } from 'react-router-dom'

import { HIDE_CONTACT_FAB_PATTERNS } from '@app/routing/LayoutFull'

import { useModals } from '@context/ModalsContext'

import { Modal } from '@ui/components/Modal'
import { SnackbarContainer } from '@ui/components/Snackbar/Snackbar'

import { PathHandler } from '@utils/PathHandler'
import { useWindowResize } from '@utils/domUtils'

import { ChatContactFab } from '@global/ChatContactFab/ChatContactFab'
import { ImpersonationBanner } from '@global/ImpersonationBanner/ImpersonationBanner'

import { type LayoutConfig } from '@hooks/useLayoutVariant'

import { Loading } from '@shared/components/Loading/Loading'
import { TopBarCondensed } from '@shared/components/Navigation/v2/TopBar/TopBarCondensed'

import styles from './LayoutFull.module.scss'

interface LayoutProps {
  layout: LayoutConfig
  setCurrentPath: (path: string) => void
}

/**
 * setting 2 — Minimal authenticated layout
 * topbarcondensed (Logo + hidesensitive + langpicker + profilemenu) + Outlet
 * no sidebar, no breadcrumbs, no notifications, no footer
 */
export const LayoutMinimalAuth: React.FC<LayoutProps> = ({ layout, setCurrentPath }) => {
  const { modals } = useModals()
  const isFlushContent = layout.content === 'flush'
  const isMobile = useWindowResize()
  const isContainerContent = layout.content === 'container'
  const isSemiContent = layout.content === 'semi'
  const mainClassName = classNames(styles.main, {
    [styles.mainPadded]: !isFlushContent,
    [styles.mainFlush]: isFlushContent,
    [styles.mainInnerSemi]: isSemiContent,
  })
  const mainInnerClassName = classNames(styles.mainInner, {
    [styles.mainInnerContainer]: isContainerContent,
  })
  const hideContactFAB =
    !isMobile && HIDE_CONTACT_FAB_PATTERNS.some((pattern) => matchPath(pattern, location.pathname))

  return (
    <div className='expander defaultBackground'>
      <ImpersonationBanner />
      <SnackbarContainer />
      <TopBarCondensed />
      <Modal.Wrapper modals={modals} />
      {!hideContactFAB && <ChatContactFab />}
      <main id='mainContainer' className={mainClassName}>
        <div className={mainInnerClassName}>
          <Suspense fallback={<Loading isLoading showLoadingIcon />}>
            <PathHandler setCurrentPath={setCurrentPath} />
            <div className={styles.outlet}>
              <Outlet />
            </div>
          </Suspense>
        </div>
      </main>
    </div>
  )
}
